/* ==========================================================================
   THE FRAGRANCE WORLD
   The hero backdrop: soft blush smoke, drawn live on a canvas by the GPU.
   Loaded by sections/fw-hero.liquid.

   Drawn rather than photographed, so it is sharp at any size and there is no
   image to download. The smoke is noise warped by more noise, the way ink
   drifts in water, and it moves slowly enough to be felt more than watched.

   The canvas is fixed to the screen (fw.css), so the smoke stays still while
   the page scrolls over it. The hero clips it, so it only ever shows inside the
   hero, and as the hero leaves, the content below takes its place.

   It keeps clear of the page where it would get in the way:
     behind the headline and buttons it thins to a faint veil, so the text keeps
     its contrast. That is measured where the headline sits before any scroll;
     the headline fades as it scrolls away (fw.js);
     the shelf stands on a sheet of paper that scrolls with it over the smoke
     (#hero .bg::after in fw.css), so the pack shots, which blend with whatever
     is behind them, keep their true colors. This measures where the pack
     shots start, into --shelf on .bg, for the sheet to start above them.
     The canvas is also plain paper from the pack shots down, following the
     scroll. The sheet already covers that, except along the hero's bottom
     edge: when it falls between pixels, the browser cuts the sheet and the
     canvas slightly differently and a one pixel line of the canvas shows.

   Cost: at most 30 frames a second, only while the hero is on screen and the
   tab is visible, at a resolution capped near 1.3 million pixels. If a device
   still cannot keep up, it draws fewer pixels.

   Under the canvas, fw.css gives .bg a still wash of the same colors. That is
   what shows before this runs, without WebGL, and while the canvas fades in.
   With reduced motion asked for, the canvas draws one still frame.
   ========================================================================== */
(() => {
  const canvas = document.querySelector('#hero .bg canvas');
  const copy = document.getElementById('herocopy');
  const shelf = document.getElementById('heroshelf');
  if (!canvas) return;
  const bg = canvas.parentElement;

  const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');
  const FRAME_MS = 1000 / 30;
  const MAX_PIXELS = 1.3e6;
  /* The smoke starts this many seconds into its drift, at a spread that looks
     settled, rather than at the very first frame of the noise. */
  const START = 40;

  const VERT = 'attribute vec2 a;void main(){gl_Position=vec4(a,0.,1.);}';

  const FRAG = `
precision highp float;
uniform vec2 uRes;    // drawing buffer, px
uniform float uTime;  // seconds
uniform vec4 uCopy;   // headline block: x0, y0, x1, y1 as fractions of the canvas, y down
uniform float uPaper; // where the pack shots start on screen now, fraction of the canvas height

// 2D simplex noise. Ian McEwan, Ashima Arts, MIT license.
vec3 mod289(vec3 x){return x-floor(x*(1./289.))*289.;}
vec2 mod289(vec2 x){return x-floor(x*(1./289.))*289.;}
vec3 permute(vec3 x){return mod289(((x*34.)+1.)*x);}
float snoise(vec2 v){
  const vec4 C=vec4(.211324865405187,.366025403784439,-.577350269189626,.024390243902439);
  vec2 i=floor(v+dot(v,C.yy));
  vec2 x0=v-i+dot(i,C.xx);
  vec2 i1=(x0.x>x0.y)?vec2(1.,0.):vec2(0.,1.);
  vec4 x12=x0.xyxy+C.xxzz;
  x12.xy-=i1;
  i=mod289(i);
  vec3 p=permute(permute(i.y+vec3(0.,i1.y,1.))+i.x+vec3(0.,i1.x,1.));
  vec3 m=max(.5-vec3(dot(x0,x0),dot(x12.xy,x12.xy),dot(x12.zw,x12.zw)),0.);
  m=m*m;m=m*m;
  vec3 x=2.*fract(p*C.www)-1.;
  vec3 h=abs(x)-.5;
  vec3 a0=x-floor(x+.5);
  m*=1.79284291400159-.85373472095314*(a0*a0+h*h);
  vec3 g;
  g.x=a0.x*x0.x+h.x*x0.y;
  g.yz=a0.yz*x12.xz+h.yz*x12.yw;
  return 130.*dot(m,g);
}

const mat2 TURN=mat2(.8,.6,-.6,.8);

// Broad strokes: three octaves are enough for the fields that bend the smoke.
float fbm3(vec2 p){
  float s=0.,a=.5;
  for(int i=0;i<3;i++){s+=a*snoise(p);p=TURN*p*2.03+vec2(1.7,9.2);a*=.5;}
  return .5+.5*s;
}
// The smoke itself: five octaves, for the fine wisps at its edges.
float fbm5(vec2 p){
  float s=0.,a=.5;
  for(int i=0;i<5;i++){s+=a*snoise(p);p=TURN*p*2.03+vec2(1.7,9.2);a*=.5;}
  return .5+.5*s;
}

void main(){
  vec2 st=vec2(gl_FragCoord.x/uRes.x,1.-gl_FragCoord.y/uRes.y);
  float aspect=uRes.x/uRes.y;
  vec2 p=vec2(st.x*aspect,st.y)*.9;
  float t=uTime*.02;

  // Two passes of domain warping bend the smoke. Adding t to y in a sample
  // carries it upward.
  vec2 q=vec2(fbm3(p+vec2(0.,t)),fbm3(p+vec2(5.2,1.3)+vec2(-.5*t,.3*t)));
  vec2 r=vec2(fbm3(p+1.5*q+vec2(1.7,9.2)+vec2(.12*t,.7*t)),
              fbm3(p+1.5*q+vec2(8.3,2.8)+vec2(-.15*t,.4*t)));
  float f=fbm5(p+2.*r+vec2(0.,.6*t));
  float w=fbm5(p*2.3+3.*r+vec2(.2*t,1.1*t));

  // A rounded box around the headline block, in units of the canvas height,
  // thins the smoke to a faint veil; it gets a little lighter toward the bottom
  // of the screen; and from the pack shots down there is none.
  vec2 c=(uCopy.xy+uCopy.zw)*.5;
  vec2 hb=(uCopy.zw-uCopy.xy)*.5*vec2(aspect,1.);
  vec2 d=abs((st-c)*vec2(aspect,1.))-hb+.06;
  float box=length(max(d,0.))+min(max(d.x,d.y),0.)-.06;
  float clearText=mix(.16,1.,smoothstep(-.04,.24,box));
  float clearShelf=1.-smoothstep(uPaper-.05,uPaper,st.y);
  float m=clearText*clearShelf*(1.-.3*st.y);

  // Veils with soft edges, and finer wisps inside them.
  float veil=smoothstep(.47,.83,f);
  float wisp=smoothstep(.52,.74,w)*smoothstep(.4,.7,f);
  float smoke=clamp(veil*.92+wisp*.36,0.,1.)*m;
  float fold=smoothstep(.55,1.2,length(q)+.5*r.x);

  vec3 paper=vec3(.980,.973,.965);  // #faf8f6, the page
  vec3 mist=vec3(.965,.914,.902);   // #f6e9e6
  vec3 blush=vec3(.922,.800,.784);  // #ebccc8
  vec3 peach=vec3(.945,.851,.820);  // #f1d9d1, a warmer note in places
  vec3 rose=vec3(.839,.651,.639);   // #d6a6a3, only where the smoke folds over itself
  vec3 tone=mix(blush,peach,smoothstep(.35,.7,r.y));
  vec3 col=mix(paper,mist,smoothstep(0.,.35,smoke));
  col=mix(col,tone,smoothstep(.2,.8,smoke));
  col=mix(col,rose,smoothstep(.65,1.,smoke)*fold);

  // Dither by half a step, so the pale gradients do not band on 8-bit screens.
  float n=fract(52.9829189*fract(dot(gl_FragCoord.xy,vec2(.06711056,.00583715))));
  gl_FragColor=vec4(col+(n-.5)/255.,1.);
}`;

  let gl, program, U, scale = Math.min(devicePixelRatio || 1, 1.5);
  /* the pack shots' top, in page px: where .bg starts, plus how far down they sit */
  let copyBox = [-10, -10, -10, -10], shelfPageY = Infinity;

  function compile(type, src) {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
    return s;
  }

  /* Builds everything the GPU holds. Run again if the browser drops the context. */
  function init() {
    gl = canvas.getContext('webgl', {
      alpha: false, antialias: false, depth: false, stencil: false,
      preserveDrawingBuffer: false, powerPreference: 'low-power',
    });
    if (!gl) return false;
    /* The noise needs real float precision. Without it, keep the still backdrop. */
    const hp = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT);
    if (!hp || hp.precision === 0) return false;
    try {
      program = gl.createProgram();
      gl.attachShader(program, compile(gl.VERTEX_SHADER, VERT));
      gl.attachShader(program, compile(gl.FRAGMENT_SHADER, FRAG));
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program));
    } catch (e) {
      console.warn('Hero smoke disabled:', e.message);
      return false;
    }
    gl.useProgram(program);
    /* one triangle that covers the whole canvas */
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const a = gl.getAttribLocation(program, 'a');
    gl.enableVertexAttribArray(a);
    gl.vertexAttribPointer(a, 2, gl.FLOAT, false, 0, 0);
    U = {
      res: gl.getUniformLocation(program, 'uRes'),
      time: gl.getUniformLocation(program, 'uTime'),
      copy: gl.getUniformLocation(program, 'uCopy'),
      paper: gl.getUniformLocation(program, 'uPaper'),
    };
    return true;
  }

  /* The headline block, as fractions of the canvas, where it sits before any
     scroll: the canvas is fixed to the top of the screen, so that is the page's
     own position. And where the pack shots start, in px from the top of .bg.
     Read from layout offsets, which ignore transforms: the copy and the pack
     shots move on scroll, and this must not follow them. */
  function measure() {
    const w = canvas.clientWidth || 1, h = canvas.clientHeight || 1;
    const grid = copy && copy.offsetParent;
    if (grid) {
      const g = grid.getBoundingClientRect();
      const x0 = g.left + copy.offsetLeft, y0 = g.top + scrollY + copy.offsetTop;
      copyBox = [x0 / w, y0 / h, (x0 + copy.offsetWidth) / w, (y0 + copy.offsetHeight) / h];
    }
    if (shelf && shelf.children.length) {
      let y = Infinity;
      for (const pack of shelf.children) y = Math.min(y, pack.offsetTop);
      const b = bg.getBoundingClientRect().top;
      const top = shelf.getBoundingClientRect().top - b + y;
      bg.style.setProperty('--shelf', `${Math.round(top)}px`);
      shelfPageY = b + scrollY + top;
    }
  }

  function size() {
    const w = canvas.clientWidth, h = canvas.clientHeight;
    if (!w || !h) return;
    const s = Math.min(scale, Math.sqrt(MAX_PIXELS / (w * h)));
    const bw = Math.max(1, Math.round(w * s)), bh = Math.max(1, Math.round(h * s));
    if (canvas.width !== bw || canvas.height !== bh) { canvas.width = bw; canvas.height = bh; }
    gl.viewport(0, 0, bw, bh);
  }

  let clock = START;
  function draw() {
    gl.uniform2f(U.res, canvas.width, canvas.height);
    gl.uniform1f(U.time, clock);
    gl.uniform4fv(U.copy, copyBox);
    gl.uniform1f(U.paper, (shelfPageY - scrollY) / (canvas.clientHeight || 1));
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  function relayout() {
    if (!gl || gl.isContextLost()) return;
    size();
    measure();
    draw();
  }

  /* --------------------------------------------------------------- the loop
     The clock only advances while frames are drawn, so the smoke carries on
     from where it was rather than jumping after the tab was hidden.          */
  let running = false, onScreen = false, raf = 0, last = 0;
  let frames = 0, spent = 0;

  function tick(now) {
    raf = requestAnimationFrame(tick);
    if (last && now - last < FRAME_MS - 2) return;
    const dt = last ? Math.min(now - last, 100) : FRAME_MS;
    last = now;
    clock += dt / 1000;
    draw();
    /* Once a second: if frames come slower than about 22 a second, the device
       is struggling, so draw a quarter fewer pixels. */
    frames++; spent += dt;
    if (spent >= 1000) {
      if (spent / frames > 45 && scale > .5) { scale = Math.max(.5, scale * .75); relayout(); }
      frames = 0; spent = 0;
    }
  }

  function update() {
    const go = onScreen && !document.hidden && !REDUCED.matches && gl && !gl.isContextLost();
    if (go === running) return;
    running = go;
    if (go) { last = 0; frames = 0; spent = 0; raf = requestAnimationFrame(tick); }
    else { cancelAnimationFrame(raf); raf = 0; }
  }

  function start() {
    if (!init()) return;
    relayout();
    canvas.classList.add('on');   /* fades in over the still backdrop (fw.css) */
    update();
  }

  canvas.addEventListener('webglcontextlost', e => {
    e.preventDefault();
    canvas.classList.remove('on');
    update();
  });
  canvas.addEventListener('webglcontextrestored', start);

  /* .bg scrolls with the hero, so this stops the smoke once the hero has gone. */
  new IntersectionObserver(es => { onScreen = es[0].isIntersecting; update(); }).observe(bg);
  document.addEventListener('visibilitychange', update);
  REDUCED.addEventListener?.('change', () => { update(); relayout(); });

  /* While the loop runs, each frame places the paper under the shelf where the
     scroll has taken it. With the smoke held still (reduced motion), redraw
     the one frame as the page scrolls instead. */
  let scrollQueued = false;
  addEventListener('scroll', () => {
    if (running || scrollQueued || !onScreen || !U || gl.isContextLost()) return;
    scrollQueued = true;
    requestAnimationFrame(() => { scrollQueued = false; draw(); });
  }, { passive: true });

  /* The headline and shelf change size as fonts and photos arrive, and the
     canvas follows the window, so measure again whenever any of them resizes. */
  if (window.ResizeObserver) {
    let queued = false;
    const ro = new ResizeObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; relayout(); });
    });
    ro.observe(canvas);
    ro.observe(bg);
    if (copy) ro.observe(copy);
    if (shelf) ro.observe(shelf);
  } else {
    addEventListener('resize', relayout);
  }

  start();
})();
